/**
 * Global, PJAX-safe back-to-top control.
 *
 * The control lives outside page content so it survives storefront swaps. Its
 * progress ring reflects how far the current document has been scrolled.
 */
(function() {
	if (window.aracScrollToTopLoaded) return;
	window.aracScrollToTopLoaded = true;

	const buttonId = "arac-scroll-to-top";
	const minimumRevealOffset = 520;
	let button = null;
	let updateFrame = null;
	let scrollFrame = null;

	function prefersReducedMotion() {
		return !!(
			window.matchMedia &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		);
	}

	function getScrollTop() {
		return Math.max(
			0,
			window.scrollY ||
			window.pageYOffset ||
			document.documentElement.scrollTop ||
			document.body.scrollTop ||
			0
		);
	}

	function getMaximumScroll() {
		const root = document.documentElement;
		const body = document.body;
		const documentHeight = Math.max(
			root.scrollHeight || 0,
			root.offsetHeight || 0,
			body.scrollHeight || 0,
			body.offsetHeight || 0
		);
		return Math.max(0, documentHeight - window.innerHeight);
	}

	function cancelScrollAnimation() {
		if (scrollFrame !== null) {
			window.cancelAnimationFrame(scrollFrame);
			scrollFrame = null;
		}
	}

	function scrollToTop() {
		cancelScrollAnimation();
		const startY = getScrollTop();
		if (startY <= 1) return;

		if (prefersReducedMotion()) {
			window.scrollTo(0, 0);
			return;
		}

		const startedAt = window.performance.now();
		const duration = Math.min(560, Math.max(380, startY * 0.12));

		function step(now) {
			const progress = Math.min(1, (now - startedAt) / duration);
			// Fast initial response with a soft, controlled landing at the top.
			const eased = 1 - Math.pow(1 - progress, 4);
			window.scrollTo(0, Math.round(startY * (1 - eased)));

			if (progress < 1) {
				scrollFrame = window.requestAnimationFrame(step);
			} else {
				scrollFrame = null;
				if (button) button.blur();
			}
		}

		scrollFrame = window.requestAnimationFrame(step);
	}

	function updateButton() {
		updateFrame = null;
		if (!button) return;

		const scrollTop = getScrollTop();
		const maximumScroll = getMaximumScroll();
		const revealOffset = Math.max(minimumRevealOffset, window.innerHeight * 0.65);
		const progress = maximumScroll > 0
			? Math.min(1, scrollTop / maximumScroll)
			: 0;

		button.style.setProperty("--arac-scroll-progress", `${Math.round(progress * 100)}%`);
		button.classList.toggle("is-visible", scrollTop >= revealOffset);
	}

	function requestUpdate() {
		if (updateFrame !== null) return;
		updateFrame = window.requestAnimationFrame(updateButton);
	}

	function createButton() {
		button = document.getElementById(buttonId);
		if (button) return button;

		const label = typeof window.__ === "function"
			? window.__("Back to top")
			: "Back to top";
		button = document.createElement("button");
		button.id = buttonId;
		button.type = "button";
		button.className = "arac-scroll-to-top";
		if (document.getElementById("mobile-bottom-nav")) {
			button.classList.add("arac-scroll-to-top--above-mobile-nav");
		}
		button.setAttribute("aria-label", label);
		button.setAttribute("title", label);
		button.innerHTML = `
			<span class="arac-scroll-to-top__icon" aria-hidden="true">
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
					<path d="M12 19V5M6.5 10.5 12 5l5.5 5.5" />
				</svg>
			</span>
		`;
		button.addEventListener("click", scrollToTop);
		document.body.appendChild(button);
		return button;
	}

	function initialize() {
		createButton();
		updateButton();
		window.addEventListener("scroll", requestUpdate, { passive: true });
		window.addEventListener("resize", requestUpdate, { passive: true });
		window.addEventListener("wheel", cancelScrollAnimation, { passive: true });
		window.addEventListener("touchstart", cancelScrollAnimation, { passive: true });
		document.addEventListener("arac:pjax:load", requestUpdate);
		window.addEventListener("pageshow", requestUpdate);
	}

	window.AracScrollToTop = {
		refresh: requestUpdate,
		scrollToTop
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initialize, { once: true });
	} else {
		initialize();
	}
})();
